import React, { useEffect, useRef, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Label, Cell } from 'recharts';

const Problem = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const chartData = [
    { year: '2019', losses: 2.3 },
    { year: '2020', losses: 3.2 },
    { year: '2021', losses: 4.1 },
    { year: '2022', losses: 5.0 },
    { year: '2023', losses: 6.1 },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <>
      <style>{`
        .recharts-wrapper *:focus {
          outline: none !important;
        }
        svg:focus {
          outline: none !important;
        }
      `}</style>
    <section className="py-8 sm:py-16 bg-white relative overflow-hidden" id="problem" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-8">
        <div className="border-2 border-gray-200 rounded-2xl p-8 bg-white shadow-sm">
          <div className="grid grid-cols-1 gap-2 sm:gap-16 items-center">
        <div className={`transition-all duration-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} text-center`}>
          <h2 className="text-3xl lg:text-4xl font-bold leading-tight mb-8 font-sans">
            Phone Scams Are Increasing — <span className="text-red-500 font-black">Seniors Are at Risk</span>
          </h2>
          <div className="hidden sm:flex gap-8 justify-center">
            <div className="flex gap-4 items-center">
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white">
                  <path d="M12 2L2 22h20L12 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 8v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="17" r="1" fill="currentColor"/>
                </svg>
              </div>
              <div className="flex flex-col">
                <h3 className="text-xl font-semibold font-sans">56 Million+ Vulnerable</h3>
              </div>
            </div>
            <div className="flex gap-4 items-center">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 8v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="16" r="1" fill="currentColor"/>
                </svg>
              </div>
              <div className="flex flex-col">
                <h3 className="text-xl font-semibold font-sans">Emotional Exploitation</h3>
              </div>
            </div>
            <div className="flex gap-4 items-center">
              <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white">
                  <circle cx="9" cy="9" r="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M9 13c-2.5 0-4 1.5-4 3v1h8v-1c0-1.5-1.5-3-4-3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="17" cy="5" r="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M17 9c-2.5 0-4 1.5-4 3v1h8v-1c0-1.5-1.5-3-4-3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="flex flex-col">
                <h3 className="text-xl font-semibold font-sans">Insufficient Family Training</h3>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 transition-all duration-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.2s' }}>
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r rounded-xl blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
            <div className="relative bg-white rounded-xl p-8 border border-red-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="mb-2">
              </div>
              <h4 className="text-2xl font-bold text-gray-800 mb-3 font-sans text-center">Severe Financial Harm</h4>
              <div className="mb-4 text-center">
                <span className="text-2xl font-bold text-red-500 font-sans">$4.9 Billion</span>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed font-sans text-center">Stolen from vulnerable seniors, reported in 2024</p>
            </div>
          </div>

          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r rounded-xl blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
            <div className="relative bg-white rounded-xl p-8 border border-orange-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="mb-2">
              </div>
              <h4 className="text-2xl font-bold text-gray-800 mb-3 font-sans text-center">Explosive Growth Rate</h4>
              <div className="mb-4 text-center">
                <span className="text-2xl font-bold text-red-500 font-sans">43% Jump</span>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed font-sans text-center">Shattering increase from 2023 to 2024</p>
              <p className="text-gray-600 text-sm leading-relaxed font-sans text-center font-bold">Scams are becoming more sophisticated with AI</p>
            </div>
          </div>

          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r rounded-xl blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
            <div className="relative bg-white rounded-xl p-8 border border-purple-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="mb-2">
              </div>
              <h4 className="text-2xl font-bold text-gray-800 mb-3 font-sans text-center">Personal Tragedy</h4>
              <div className="mb-4 text-center">
                <span className="text-2xl font-bold text-red-500 font-sans">$83K+ Average Loss</span>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed font-sans text-center">Average victim loses decades of hard work</p>
            </div>
          </div>
        </div>
        </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default Problem;
